/**
 * DistributeModal Component
 *
 * Admin modal for distributing a template to multiple users.
 * Creates copies of the template with different ownerIds.
 *
 * @module components/claude-templates/DistributeModal
 */

import { useState, useEffect, memo, useCallback } from 'react';
import { Share2, Loader2, X, User, CheckCircle, AlertCircle } from 'lucide-react';
import { Avatar } from '../common/Avatar.jsx';

const API_BASE = '/api';

/**
 * Fetch all users
 */
async function fetchUsers() {
  const res = await fetch(`${API_BASE}/users`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch users');
  const data = await res.json();
  return data.data || [];
}

/**
 * Distribute template to users
 */
async function distributeTemplate(templateId, userIds, newName) {
  const res = await fetch(`${API_BASE}/claude-templates/${templateId}/distribute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ userIds, newName: newName || undefined })
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to distribute template');
  }
  return res.json();
}

/**
 * UserCheckboxItem component
 */
const UserCheckboxItem = memo(function UserCheckboxItem({
  user,
  isSelected,
  onToggle,
  status,
  isDisabled
}) {
  const statusIcon = {
    pending: null,
    processing: <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />,
    success: <CheckCircle className="w-4 h-4 text-green-400" />,
    error: <AlertCircle className="w-4 h-4 text-red-400" />
  }[status || 'pending'];

  return (
    <label
      className={`
        flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
        ${isSelected
          ? 'bg-indigo-600/20 border-indigo-500/50'
          : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
        }
        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggle(user.id)}
        disabled={isDisabled}
        className="w-4 h-4 rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-900"
      />
      <Avatar
        src={user.avatarUrl}
        name={user.displayName || user.email}
        size="sm"
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-200 truncate">
          {user.displayName || user.email}
        </div>
        {user.email && user.displayName && (
          <div className="text-xs text-gray-500 truncate">
            {user.email}
          </div>
        )}
      </div>
      {statusIcon && (
        <div className="flex-shrink-0">
          {statusIcon}
        </div>
      )}
    </label>
  );
});

/**
 * DistributeModal Component
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {object} props.template - Template to distribute
 * @param {function} props.onClose - Close callback
 * @param {function} props.onComplete - Callback when distribution completes
 */
const DistributeModal = memo(function DistributeModal({
  isOpen,
  template,
  onClose,
  onComplete
}) {
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [newName, setNewName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Load users when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUserIds(new Set());
      setNewName('');
      setError(null);
      setResult(null);
      loadUsers();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleToggleUser = useCallback((userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(users.map(u => u.id)));
    }
  }, [users, selectedUserIds.size]);

  const handleDistribute = useCallback(async () => {
    if (selectedUserIds.size === 0 || !template) return;

    setIsProcessing(true);
    setError(null);

    try {
      const userIds = Array.from(selectedUserIds);
      const response = await distributeTemplate(template.id, userIds, newName);
      setResult({
        success: response.created || userIds.length,
        templateName: newName || template.name
      });
      if (onComplete) {
        onComplete(response.created || userIds.length);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedUserIds, template, newName, onComplete]);

  const isAllSelected = selectedUserIds.size === users.length;
  const hasSelection = selectedUserIds.size > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-indigo-400" />
            <h3 className="text-lg font-semibold text-white">Distribute Template</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Template Info */}
        <div className="px-6 py-3 bg-gray-800/50 border-b border-gray-700 flex-shrink-0">
          <span className="text-sm text-gray-400">
            Template: <span className="text-white font-medium">{template?.name}</span>
          </span>
        </div>

        {/* Optional New Name */}
        <div className="px-6 py-3 border-b border-gray-700 flex-shrink-0">
          <label className="block text-sm text-gray-400 mb-2">
            New template name (optional)
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={template?.name}
            disabled={isProcessing || result}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          />
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
            </div>
          ) : error && !result ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400 opacity-50" />
              <p className="text-red-400">{error}</p>
            </div>
          ) : result ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-green-400 font-medium">
                Distributed to {result.success} user{result.success !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Template "{result.templateName}" was copied to selected users.
              </p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="mb-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  disabled={isProcessing}
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50"
                >
                  {isAllSelected ? 'Deselect All' : 'Select All'} ({users.length} users)
                </button>
              </div>

              {/* User Checkboxes */}
              <div className="space-y-2">
                {users.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No users available</p>
                  </div>
                ) : (
                  users.map((user) => (
                    <UserCheckboxItem
                      key={user.id}
                      user={user}
                      isSelected={selectedUserIds.has(user.id)}
                      onToggle={handleToggleUser}
                      isDisabled={isProcessing}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-500">
            {result ? (
              <span className="text-green-400">Distribution complete</span>
            ) : hasSelection ? (
              `${selectedUserIds.size} user${selectedUserIds.size > 1 ? 's' : ''} selected`
            ) : (
              'Select users to distribute to'
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {result ? 'Close' : 'Cancel'}
            </button>
            {!result && (
              <button
                type="button"
                onClick={handleDistribute}
                disabled={isProcessing || !hasSelection}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
                Distribute to {selectedUserIds.size || '...'} User{selectedUserIds.size !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default DistributeModal;
